import { useEffect, useState } from 'react';

import {
  Headphones,
  PhoneCall,
  PhoneForwarded,
  PhoneOff,
  Radio,
  RotateCcw,
  Send,
  Square,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  useLiveCallMonitor,
  type LiveMonitorConnectionState,
  type LiveMonitorTranscriptEntry,
} from '@/hooks/useLiveCallMonitor';
import { callsApi } from '@/lib/api/calls.api';
import { useAuthStore } from '@/stores/auth.store';

import type { CallStatus, CallTransport } from '@repo/shared-types';

const LIVE_STATUSES: CallStatus[] = ['IN_PROGRESS'];

function connectionBadgeMeta(state: LiveMonitorConnectionState): {
  label: string;
  className: string;
} {
  switch (state) {
    case 'connected':
      return {
        label: 'Connected',
        className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
      };
    case 'connecting':
      return {
        label: 'Connecting',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
      };
    case 'reconnecting':
      return {
        label: 'Reconnecting',
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
      };
    case 'ended':
      return {
        label: 'Call Ended',
        className: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
      };
    case 'not_live':
      return {
        label: 'Not Live',
        className: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
      };
    case 'error':
      return {
        label: 'Error',
        className: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
      };
    case 'idle':
    default:
      return {
        label: 'Idle',
        className: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
      };
  }
}

function renderTranscriptSpeaker(message: LiveMonitorTranscriptEntry): string {
  return message.role === 'user' ? 'Customer' : 'Agent';
}

interface LiveCallMonitorCardProps {
  callId: string;
  status: CallStatus;
  transport?: CallTransport | null;
}

export function LiveCallMonitorCard({ callId, status, transport }: LiveCallMonitorCardProps) {
  const { toast } = useToast();
  const userRole = useAuthStore((state) => state.user?.role);
  const isAdmin = userRole === 'admin';
  const [isHandoffPending, setIsHandoffPending] = useState(false);
  const [isReturnPending, setIsReturnPending] = useState(false);
  const [isHangupPending, setIsHangupPending] = useState(false);
  const [operatorInput, setOperatorInput] = useState('');

  const {
    isListening,
    isTakeoverActive,
    connectionState,
    channel,
    transcript,
    error,
    startListening,
    stopListening,
    startTakeover,
    stopTakeover,
    sendOperatorMessage,
    clearTranscript,
  } = useLiveCallMonitor(callId);

  // The backend reports the channel once subscribed. Until then we fall back to
  // the call's transport so the right takeover UI shows before connecting.
  //  - text  : WhatsApp text chat → pause AI + type replies
  //  - channel voice : WhatsApp voice / WebRTC → listen + speak via WebRTC (no Twilio leg control)
  //  - twilio voice  : PSTN → listen + speak via WebRTC + Twilio handoff/return/hangup
  const isTextChannel = channel === 'text';
  const isPstn = transport === 'PSTN' || transport === undefined || transport === null;
  const isChannelVoice = !isTextChannel && !isPstn;
  const isLiveCall = LIVE_STATUSES.includes(status);

  const handleSendOperatorMessage = () => {
    if (sendOperatorMessage(operatorInput)) {
      setOperatorInput('');
    }
  };

  useEffect(() => {
    if (!isLiveCall && isListening) {
      stopListening();
    }
  }, [isLiveCall, isListening, stopListening]);

  const handleManualHandoff = async () => {
    setIsHandoffPending(true);
    try {
      await callsApi.manualHandoffCall(callId);
      await startTakeover();

      toast({
        variant: 'success',
        title: 'Takeover active',
        description: 'You can now talk to the customer directly through your browser microphone.',
      });
    } catch {
      // API interceptor already displays details.
    } finally {
      setIsHandoffPending(false);
    }
  };

  const handleReturnToAi = async () => {
    setIsReturnPending(true);
    try {
      stopTakeover();
      await callsApi.manualReturnToAi(callId);
      toast({
        variant: 'success',
        title: 'Returned to AI',
        description: 'The call is being redirected back to the AI agent.',
      });
    } catch {
      // API interceptor already displays details.
    } finally {
      setIsReturnPending(false);
    }
  };

  const handleHangup = async () => {
    setIsHangupPending(true);
    try {
      stopTakeover();
      await callsApi.manualHangupCall(callId);
      toast({
        variant: 'success',
        title: 'Hangup requested',
        description: 'The active call is being ended.',
      });
    } catch {
      // API interceptor already displays details.
    } finally {
      setIsHangupPending(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const badgeMeta = connectionBadgeMeta(connectionState);

  return (
    <Card className="border-cyan-200 dark:border-cyan-800">
      <CardHeader className="rounded-t-lg bg-cyan-50 dark:bg-cyan-950/30">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Headphones className="h-5 w-5 text-cyan-700 dark:text-cyan-300" />
            <CardTitle className="text-cyan-900 dark:text-cyan-100">Live Call Monitor</CardTitle>
          </div>
          <Badge className={badgeMeta.className}>{badgeMeta.label}</Badge>
        </div>
        <CardDescription>
          {isTextChannel
            ? 'Follow the live conversation and take over to reply to the customer yourself.'
            : 'Listen to both call audio channels and view live final transcript turns.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => {
              if (isListening) {
                stopListening();
              } else {
                void startListening();
              }
            }}
            disabled={!isLiveCall && !isListening}
          >
            {isListening ? (
              <>
                <Square className="mr-2 h-4 w-4" />
                {isTextChannel ? 'Stop Following' : 'Stop Listening'}
              </>
            ) : (
              <>
                <Radio className="mr-2 h-4 w-4" />
                {isTextChannel ? 'Follow Live' : 'Listen Live'}
              </>
            )}
          </Button>

          <Button variant="outline" onClick={clearTranscript} disabled={transcript.length === 0}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Clear Transcript
          </Button>
        </div>

        {isTextChannel ? (
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">Operator Takeover</p>

            <div className="flex flex-wrap gap-2">
              {isTakeoverActive ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    stopTakeover();
                  }}
                >
                  <PhoneForwarded className="mr-2 h-4 w-4" />
                  Hand Back To AI
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    void startTakeover();
                  }}
                  disabled={!isLiveCall || !isListening}
                >
                  <PhoneCall className="mr-2 h-4 w-4" />
                  Take Over Chat
                </Button>
              )}
            </div>

            <p className="text-muted-foreground text-xs">
              {isTakeoverActive
                ? 'You are handling this conversation. The AI will not reply until you hand it back.'
                : 'Pause the AI and reply to the customer yourself. Start following the conversation first.'}
            </p>

            {isTakeoverActive ? (
              <div className="flex gap-2">
                <Input
                  value={operatorInput}
                  onChange={(event) => setOperatorInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSendOperatorMessage();
                    }
                  }}
                  placeholder="Type a message to the customer..."
                />
                <Button onClick={handleSendOperatorMessage} disabled={!operatorInput.trim()}>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </Button>
              </div>
            ) : null}
          </div>
        ) : isChannelVoice ? (
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">Voice Takeover</p>

            <div className="flex flex-wrap gap-2">
              {isTakeoverActive ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    stopTakeover();
                  }}
                >
                  <PhoneForwarded className="mr-2 h-4 w-4" />
                  Hand Back To AI
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    void startTakeover();
                  }}
                  disabled={!isLiveCall || !isListening}
                >
                  <PhoneCall className="mr-2 h-4 w-4" />
                  Take Over (Speak)
                </Button>
              )}
            </div>

            <p className="text-muted-foreground text-xs">
              {isTakeoverActive
                ? 'You are speaking with the customer. The AI is muted until you hand it back.'
                : 'Speak to the customer through your browser microphone. Start listening first.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">WebRTC Takeover</p>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  void handleManualHandoff();
                }}
                disabled={
                  !isLiveCall ||
                  isTakeoverActive ||
                  isHandoffPending ||
                  isReturnPending ||
                  isHangupPending
                }
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                {isHandoffPending ? 'Starting...' : 'Take Over In Browser'}
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  stopTakeover();
                }}
                disabled={!isTakeoverActive || isReturnPending || isHangupPending}
              >
                <Square className="mr-2 h-4 w-4" />
                Stop Talking
              </Button>
            </div>

            <p className="text-muted-foreground text-xs">
              No phone number is required. Click Take Over In Browser and speak directly using your
              microphone.
            </p>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  void handleReturnToAi();
                }}
                disabled={!isLiveCall || isHandoffPending || isReturnPending || isHangupPending}
              >
                <PhoneForwarded className="mr-2 h-4 w-4" />
                {isReturnPending ? 'Returning...' : 'Return To AI Now'}
              </Button>

              <Button
                variant="destructive"
                onClick={() => {
                  void handleHangup();
                }}
                disabled={!isLiveCall || isHandoffPending || isReturnPending || isHangupPending}
              >
                <PhoneOff className="mr-2 h-4 w-4" />
                {isHangupPending ? 'Hanging up...' : 'Hang Up Call'}
              </Button>
            </div>
          </div>
        )}

        {!isLiveCall ? (
          <p className="text-muted-foreground text-sm">
            The call is not currently in progress. Live monitoring is available only while the call
            is active.
          </p>
        ) : null}

        {error ? <p className="text-sm text-red-600 dark:text-red-300">{error}</p> : null}

        <div className="space-y-2">
          <p className="text-sm font-medium">Live Transcript ({transcript.length})</p>

          {transcript.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No transcript messages yet. Start listening while the call is live.
            </p>
          ) : (
            <div className="max-h-80 space-y-3 overflow-y-auto pr-2">
              {transcript.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground dark:text-white'
                        : 'bg-muted dark:text-white'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
                      <span>{renderTranscriptSpeaker(message)}</span>
                      {message.replay ? <span className="opacity-80">Replay</span> : null}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
